import React, {createContext, useContext, useEffect, useMemo, useState, type ReactNode} from 'react';
import {getSession, SessionProvider, useSession} from 'next-auth/react';
import type {Session} from 'next-auth';
import {useAtom} from 'jotai';
import {authorizationAtom} from '@/store/authorization-atom';
import {useCurrentCustomerLazyQuery} from '@/__generated__/src/framework/graphql/gql/auth.graphql';
import {clearAuthData, getCustomerAccessToken} from '@/_application/modules/auth/model/auth-token';
import {customerStoreAPI} from '@kidslabs/module-customer';
import {Customer} from '@kidslabs/module-customer/types';
import {authStore$} from '@kidslabs/module-auth';

export interface State {
    isAuthenticated: boolean;
}

const initialState = {
    isAuthenticated: false,
};

export const SessionAuthContext = createContext<State | any>(initialState);

SessionAuthContext.displayName = 'SessionAuthContext';

interface SessionAuthProviderProps {
    session?: Session | null;
    children: ReactNode;
}

/**
 * Inner component that handles auth state management
 * Must be wrapped inside SessionProvider to use useSession
 */
const SessionAuthInner = ({children}: {children: ReactNode}) => {
    const [isAuthenticated, updateIsAuthenticated] = useState(false);
    const [, setAuthorized] = useAtom(authorizationAtom);
    const [getCustomerProfile] = useCurrentCustomerLazyQuery();
    const {status} = useSession();

    useEffect(() => {
        getSession()
            .then(async session => {
                if (session && session.user?.email) {
                    getCustomerProfile({
                        fetchPolicy: 'no-cache',
                    })
                        .then(async results => {
                            if (results?.data?.customer) {
                                // check if access token is valid
                                setAuthorized(true);
                                updateIsAuthenticated(true);
                                authStore$.isAuthenticated.set(true);
                                // Set currentCustomer to store - matches mobile's customerProfile$.currentCustomer.set()
                                customerStoreAPI.actions.setCurrentCustomer(results.data.customer as Customer);
                            } else {
                                setAuthorized(false);
                                updateIsAuthenticated(false);
                                authStore$.isAuthenticated.set(false);
                                // Clear currentCustomer when auth fails
                                customerStoreAPI.actions.setCurrentCustomer(undefined);
                                await clearAuthData();
                            }
                        })
                        .catch(async () => {
                            setAuthorized(false);
                            updateIsAuthenticated(false);
                            authStore$.isAuthenticated.set(false);
                            // Clear currentCustomer on error
                            customerStoreAPI.actions.setCurrentCustomer(undefined);
                            await clearAuthData();
                        });
                } else {
                    setAuthorized(false);
                    updateIsAuthenticated(false);
                    authStore$.isAuthenticated.set(false);
                    // Clear currentCustomer when no session
                    customerStoreAPI.actions.setCurrentCustomer(undefined);
                    await clearAuthData();
                }
            })
            .catch(async () => {
                setAuthorized(false);
                updateIsAuthenticated(false);
                authStore$.isAuthenticated.set(false);
                // Clear currentCustomer on session error
                customerStoreAPI.actions.setCurrentCustomer(undefined);
                await clearAuthData();
            });
    }, []);

    useEffect(() => {
        if (status !== 'loading') {
            const isAuth = status === 'authenticated';
            updateIsAuthenticated(isAuth);
            setAuthorized(isAuth);
            authStore$.isAuthenticated.set(isAuth);
        }
    }, [status]);

    const value = useMemo(
        () => ({
            isAuthenticated,
            updateIsAuthenticated,
        }),
        [isAuthenticated],
    );

    return <SessionAuthContext.Provider value={value}>{children}</SessionAuthContext.Provider>;
};

/**
 * Handler for session authentication
 * Combines NextAuth SessionProvider with custom auth state management
 * @param props
 * @constructor
 */
export const SessionAuthProvider = ({children, session}: SessionAuthProviderProps) => {
    return (
        <SessionProvider session={session} basePath="/api/auth">
            <SessionAuthInner>{children}</SessionAuthInner>
        </SessionProvider>
    );
};

export const useSessionAuth = () => {
    const context = useContext(SessionAuthContext);
    if (context === undefined) {
        throw new Error(`useSessionAuth must be used within a SessionAuthProvider`);
    }
    return context;
};
